import {
    NOTIFICATION_CATALOG,
    type NotificationTypeKey,
    type ToastNotificationTypeKey,
} from '@apps/web/lib/notification_catalog.generated';

export const STATUS_TO_PREFERENCE_KEY = {
    callout_shift_accepted: 'callout_shift_accepted',
    callout_shift_needs_review: 'callout_shift_needs_review',
    clock_in_out: 'clock_in_out',
    missed_clock_in: 'missed_clock_in',
    missed_clock_out: 'missed_clock_out',
    timely_clock_in: 'timely_clock_in',
    timely_clock_out: 'timely_clock_out',
    confirmed: 'shift_confirmation_confirmed',
    shift_cancellation: 'shift_cancellation',
    declined: 'shift_confirmation_declined',
    needs_review: 'shift_confirmation_needs_review',
    no_response: 'shift_confirmation_no_response',
    outreach_finished_unfilled: 'outreach_finished_unfilled',
    outreach_finished_filled: 'outreach_finished_filled',
    activity_action_required: 'activity_action_required',
    activity_urgent: 'activity_urgent',
    after_hours_receptionist_digest: 'after_hours_receptionist_digest',
    after_hours_receptionist_urgent: 'after_hours_receptionist_urgent',
    call_summary: 'call_summary',
    inbound_sms_received: 'inbound_sms_received',
    conversation_assigned: 'conversation_assigned',
    conversation_tagged: 'conversation_tagged',
    agent_reminder: 'agent_reminder',
    shift_confirmation_staged_messages_reminder:
        'shift_confirmation_staged_messages_reminder',
} as const;

type NotificationPreferenceChannel = 'inapp';

export type NotificationPreferenceKey =
    | NotificationTypeKey
    | ToastNotificationTypeKey;

function isNotificationTypeKey(
    preferenceKey: NotificationPreferenceKey,
): preferenceKey is NotificationTypeKey {
    return Object.prototype.hasOwnProperty.call(
        NOTIFICATION_CATALOG,
        preferenceKey,
    );
}

/**
 * The default a channel falls back to when no stored preference exists.
 *
 * Server-backed types read the generated catalog, so the client can never
 * assume a channel the backend defaults off. A client-raised toast key has
 * no server preference row at all, so it stays off.
 */
function getDefaultNotificationChannelValue(
    preferenceKey: NotificationPreferenceKey,
    channel: NotificationPreferenceChannel,
): boolean {
    return isNotificationTypeKey(preferenceKey)
        ? NOTIFICATION_CATALOG[preferenceKey].defaultChannels[channel]
        : false;
}

type NotificationChannelScheduleRule = {
    readonly end_time?: unknown;
    readonly start_time?: unknown;
    readonly weekdays?: unknown;
};

type NotificationChannelMonthlyScheduleRule = {
    readonly days_of_month?: unknown;
    readonly end_time?: unknown;
    readonly start_time?: unknown;
};

type NotificationChannelSchedule = NotificationChannelScheduleRule & {
    readonly rules?: unknown;
    readonly type?: unknown;
};

type NotificationPreferencesLikeValue = Partial<
    Record<
        NotificationPreferenceKey,
        Partial<Record<NotificationPreferenceChannel, boolean>> | boolean
    >
> & {
    readonly channel_schedules?: Partial<
        Record<
            NotificationPreferenceChannel,
            NotificationChannelSchedule | null
        >
    > | null;
    readonly toast_global_enabled?: boolean;
};

type NotificationPreferencesLike =
    | NotificationPreferencesLikeValue
    | null
    | undefined;

const SHORT_WEEKDAY_TO_MONDAY_FIRST_INDEX: Readonly<Record<string, number>> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
};

function getPreviousLocalMonthDay(
    year: number,
    month: number,
    monthDay: number,
): number {
    // Inputs already describe the schedule timezone's local date.
    const previousDate = new Date(Date.UTC(year, month - 1, monthDay));
    previousDate.setUTCDate(previousDate.getUTCDate() - 1);
    return previousDate.getUTCDate();
}

function parseScheduleTime(value: unknown): number | null {
    if (typeof value !== 'string') {
        return null;
    }

    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    if (!match) {
        return null;
    }

    return Number(match[1]) * 60 + Number(match[2]);
}

function parseScheduleWeekdays(value: unknown): Set<number> | null {
    if (!Array.isArray(value)) {
        return null;
    }

    const weekdays = new Set<number>();
    for (const item of value) {
        if (
            typeof item !== 'number' ||
            !Number.isInteger(item) ||
            item < 0 ||
            item > 6
        ) {
            return null;
        }
        weekdays.add(item);
    }
    return weekdays.size > 0 ? weekdays : null;
}

function parseScheduleDaysOfMonth(value: unknown): Set<number> | null {
    if (!Array.isArray(value)) {
        return null;
    }

    const daysOfMonth = new Set<number>();
    for (const item of value) {
        if (
            typeof item !== 'number' ||
            !Number.isInteger(item) ||
            item < 1 ||
            item > 31
        ) {
            return null;
        }
        daysOfMonth.add(item);
    }
    return daysOfMonth.size > 0 ? daysOfMonth : null;
}

function isNotificationChannelScheduleRule(
    value: unknown,
): value is NotificationChannelScheduleRule {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const record = value as NotificationChannelScheduleRule;
    return (
        Array.isArray(record.weekdays) &&
        typeof record.start_time === 'string' &&
        typeof record.end_time === 'string'
    );
}

function isNotificationChannelMonthlyScheduleRule(
    value: unknown,
): value is NotificationChannelMonthlyScheduleRule {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const record = value as NotificationChannelMonthlyScheduleRule;
    return (
        Array.isArray(record.days_of_month) &&
        typeof record.start_time === 'string' &&
        typeof record.end_time === 'string'
    );
}

function getNotificationChannelScheduleRules(
    schedule: NotificationChannelSchedule,
): {
    readonly type: 'weekly' | 'monthly';
    readonly rules: ReadonlyArray<
        NotificationChannelScheduleRule | NotificationChannelMonthlyScheduleRule
    >;
} | null {
    const scheduleType = schedule.type === 'monthly' ? 'monthly' : 'weekly';
    if (Array.isArray(schedule.rules)) {
        const ruleIsValid =
            scheduleType === 'monthly'
                ? isNotificationChannelMonthlyScheduleRule
                : isNotificationChannelScheduleRule;
        if (!schedule.rules.every(ruleIsValid)) {
            return null;
        }
        return { type: scheduleType, rules: schedule.rules };
    }
    if (isNotificationChannelScheduleRule(schedule)) {
        return { type: 'weekly', rules: [schedule] };
    }
    return null;
}

function getZonedScheduleDateParts(
    date: Date,
    timezone: string | null | undefined,
): {
    readonly minute: number;
    readonly monthDay: number;
    readonly previousMonthDay: number;
    readonly weekday: number;
} {
    const resolvedTimezone = timezone ?? 'UTC';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'numeric',
            hour: '2-digit',
            hourCycle: 'h23',
            day: 'numeric',
            minute: '2-digit',
            timeZone: resolvedTimezone,
            weekday: 'short',
        }).formatToParts(date);
        const values = Object.fromEntries(
            parts.map((part) => [part.type, part.value]),
        );
        const weekday =
            SHORT_WEEKDAY_TO_MONDAY_FIRST_INDEX[values['weekday'] ?? ''];
        const year = Number(values['year']);
        const month = Number(values['month']);
        const monthDay = Number(values['day']);
        const hour = Number(values['hour']);
        const minute = Number(values['minute']);
        if (
            weekday !== undefined &&
            Number.isInteger(year) &&
            Number.isInteger(month) &&
            Number.isInteger(monthDay) &&
            Number.isInteger(hour) &&
            Number.isInteger(minute)
        ) {
            return {
                minute: hour * 60 + minute,
                monthDay,
                previousMonthDay: getPreviousLocalMonthDay(
                    year,
                    month,
                    monthDay,
                ),
                weekday,
            };
        }
    } catch {
        if (resolvedTimezone !== 'UTC') {
            return getZonedScheduleDateParts(date, 'UTC');
        }
    }

    return {
        minute: date.getUTCHours() * 60 + date.getUTCMinutes(),
        monthDay: date.getUTCDate(),
        previousMonthDay: getPreviousLocalMonthDay(
            date.getUTCFullYear(),
            date.getUTCMonth() + 1,
            date.getUTCDate(),
        ),
        weekday: (date.getUTCDay() + 6) % 7,
    };
}

function isNotificationChannelScheduleRuleActive(
    rule: NotificationChannelScheduleRule,
    options: {
        readonly scheduleDateParts: {
            readonly minute: number;
            readonly weekday: number;
        };
    },
): boolean {
    const weekdays = parseScheduleWeekdays(rule.weekdays);
    const startMinute = parseScheduleTime(rule.start_time);
    const endMinute = parseScheduleTime(rule.end_time);
    if (weekdays === null || startMinute === null || endMinute === null) {
        return false;
    }

    const { weekday: currentWeekday, minute: currentMinute } =
        options.scheduleDateParts;
    const previousWeekday = (currentWeekday + 6) % 7;

    if (startMinute === endMinute) {
        return weekdays.has(currentWeekday);
    }
    if (startMinute < endMinute) {
        return (
            weekdays.has(currentWeekday) &&
            currentMinute >= startMinute &&
            currentMinute < endMinute
        );
    }
    return (
        (weekdays.has(currentWeekday) && currentMinute >= startMinute) ||
        (weekdays.has(previousWeekday) && currentMinute < endMinute)
    );
}

function isNotificationChannelMonthlyScheduleRuleActive(
    rule: NotificationChannelMonthlyScheduleRule,
    options: {
        readonly scheduleDateParts: {
            readonly monthDay: number;
            readonly previousMonthDay: number;
            readonly minute: number;
        };
    },
): boolean {
    const daysOfMonth = parseScheduleDaysOfMonth(rule.days_of_month);
    const startMinute = parseScheduleTime(rule.start_time);
    const endMinute = parseScheduleTime(rule.end_time);
    if (daysOfMonth === null || startMinute === null || endMinute === null) {
        return false;
    }

    const {
        monthDay: currentMonthDay,
        previousMonthDay,
        minute: currentMinute,
    } = options.scheduleDateParts;

    if (startMinute === endMinute) {
        return daysOfMonth.has(currentMonthDay);
    }
    if (startMinute < endMinute) {
        return (
            daysOfMonth.has(currentMonthDay) &&
            currentMinute >= startMinute &&
            currentMinute < endMinute
        );
    }
    return (
        (daysOfMonth.has(currentMonthDay) && currentMinute >= startMinute) ||
        (daysOfMonth.has(previousMonthDay) && currentMinute < endMinute)
    );
}

function isNotificationChannelScheduleActive(
    schedule: NotificationChannelSchedule | null | undefined,
    options: {
        readonly now: Date;
        readonly timezone?: string | null;
    },
): boolean {
    if (!schedule) {
        return true;
    }

    const scheduleRules = getNotificationChannelScheduleRules(schedule);
    if (!scheduleRules || scheduleRules.rules.length === 0) {
        return false;
    }

    const scheduleDateParts = getZonedScheduleDateParts(
        options.now,
        options.timezone,
    );
    if (scheduleRules.type === 'monthly') {
        return scheduleRules.rules.some((rule) =>
            isNotificationChannelMonthlyScheduleRuleActive(
                rule as NotificationChannelMonthlyScheduleRule,
                {
                    scheduleDateParts: {
                        monthDay: scheduleDateParts.monthDay,
                        previousMonthDay: scheduleDateParts.previousMonthDay,
                        minute: scheduleDateParts.minute,
                    },
                },
            ),
        );
    }
    return scheduleRules.rules.some((rule) =>
        isNotificationChannelScheduleRuleActive(
            rule as NotificationChannelScheduleRule,
            { scheduleDateParts },
        ),
    );
}

export function getNotificationPreferenceKeyForStatus(
    status: string | null,
): NotificationPreferenceKey | null {
    if (!status) {
        return null;
    }

    return (
        ((STATUS_TO_PREFERENCE_KEY as Record<string, string | undefined>)[
            status
        ] as NotificationPreferenceKey | undefined) ?? null
    );
}

export function isNotificationChannelEnabled(
    preferences: NotificationPreferencesLike,
    status: string | null,
    channel: NotificationPreferenceChannel,
    options?: {
        readonly defaultEnabled?: boolean;
        readonly now?: Date;
        readonly skipSchedule?: boolean;
        readonly timezone?: string | null;
    },
): boolean {
    if (!status) {
        return options?.defaultEnabled ?? true;
    }

    const preferenceKey = getNotificationPreferenceKeyForStatus(status);
    if (!preferenceKey) {
        return options?.defaultEnabled ?? true;
    }

    return isNotificationPreferenceKeyChannelEnabled(
        preferences,
        preferenceKey,
        channel,
        options,
    );
}

export function isNotificationPreferenceKeyChannelEnabled(
    preferences: NotificationPreferencesLike,
    preferenceKey: NotificationPreferenceKey,
    channel: NotificationPreferenceChannel,
    options?: {
        readonly now?: Date;
        readonly skipSchedule?: boolean;
        readonly timezone?: string | null;
    },
): boolean {
    const defaultChannelValue = getDefaultNotificationChannelValue(
        preferenceKey,
        channel,
    );

    if (!preferences) {
        return defaultChannelValue;
    }

    const preference = preferences[preferenceKey];
    if (!preference || typeof preference !== 'object') {
        if (!defaultChannelValue) {
            return false;
        }
        if (options?.skipSchedule) {
            return true;
        }
        return isNotificationChannelScheduleActive(
            preferences.channel_schedules?.[channel],
            {
                now: options?.now ?? new Date(),
                timezone: options?.timezone,
            },
        );
    }

    const channelEnabled = preference[channel] ?? defaultChannelValue;
    if (!channelEnabled) {
        return false;
    }
    if (options?.skipSchedule) {
        return true;
    }
    return isNotificationChannelScheduleActive(
        preferences.channel_schedules?.[channel],
        {
            now: options?.now ?? new Date(),
            timezone: options?.timezone,
        },
    );
}
